import { dashboardApi } from '@/store/api/base/api.base';
import { walkPages } from '@/store/api/base/api.walk';
import { dropTag, writeBoards, writeProjects, writeTags, writeTasks } from '@/store/api/work/work.cache';
import type { TypeBoard } from '@/types/work/types.work.board';
import type { TypeNote } from '@/types/work/types.work.note';
import type { TypeProject } from '@/types/work/types.work.project';
import type { TypeTag, TypeTagColor } from '@/types/work/types.work.tag';
import type { TypeTask, TypeTaskEdit, TypeTaskPriority, TypeTaskState } from '@/types/work/types.work.task';
import { dropById } from '@/store/api/base/api.cache';
import type { TypeBoardInput, TypeProjectInput, TypeTaskPatch } from '@/types/work/types.work.input';

const PAGE_SIZE = 200;

const MAX_PAGES = 40;

const readAll = <T>(url: string, params: Record<string, unknown> = {}) => walkPages<T>({
    url, fallback: 'Non è stato possibile caricare il lavoro', params, pageSize: PAGE_SIZE, maxPages: MAX_PAGES,
});

type TypeTaskInput = {
    projectId: string;
    parentId?: string | null;
    title: string;
    description?: string;
    notes?: string;
    state: TypeTaskState;
    priority?: TypeTaskPriority;
    assigneeId?: string | null;
    dueAt?: string | null;
    estimateHours?: number | null;
    tags?: string[];
};

const projectBody = (project: TypeProjectInput) => ({
    ...project,
    serviceId: project.serviceId ?? '',
    customerId: project.customerId ?? '',
    dueAt: project.dueAt ?? '',
    boardId: project.boardId ?? '',
    ownerId: project.ownerId ?? '',
});

const boardBody = (board: TypeBoardInput) => ({
    name: board.name,
    hint: board.hint,
    columns: board.columns.map((column) => ({
        id: column.id ?? '',
        label: column.label,
        hint: column.hint,
        color: column.color ?? '',
        status: column.status ?? '',
    })),
});

const noteBody = (note: TypeNote) => ({
    ...note,
    map: {
        type: note.map.type ?? 'data',
        edgeStyle: note.map.edgeStyle ?? 'curve',
        groups: note.map.groups ?? [],
        nodes: note.map.nodes.map((node) => ({
            ...node, stack: node.stack ?? [], w: node.w ?? 0, tone: node.tone ?? 'warning', group: node.group ?? '',
        })),
        edges: note.map.edges.map((edge) => ({ ...edge, fromField: edge.fromField ?? '' })),
        views: note.map.views ?? [],
    },
    checklist: { items: (note.checklist?.items ?? []).map((item) => ({ ...item, taskId: item.taskId ?? '' })) },
    table: note.table ?? { columns: [], rows: [] },
    meeting: {
        heldAt: note.meeting?.heldAt ?? '',
        attendeeIds: note.meeting?.attendeeIds ?? [],
        decisions: note.meeting?.decisions ?? [],
        actions: (note.meeting?.actions ?? []).map((action) => ({ ...action, ownerId: action.ownerId ?? '', dueAt: action.dueAt ?? '', taskId: action.taskId ?? '' })),
    },
});

const taskPatchBody = (task: TypeTaskPatch) => ({
    ...task,
    ...(task.assigneeId === null ? { assigneeId: '' } : {}),
    ...(task.dueAt === null ? { dueAt: '' } : {}),
    ...(task.notebook ? { notebook: task.notebook.map(noteBody) } : {}),
});

const taskEditBody = (task: TypeTaskEdit) => ({
    ...task,
    ...(task.assigneeId === null ? { assigneeId: '' } : {}),
    ...(task.dueAt === null ? { dueAt: '' } : {}),
});

export const workApi = dashboardApi.injectEndpoints({
    endpoints: (build) => ({
        listProjects: build.query<TypeProject[], void>({
            queryFn: () => readAll<TypeProject>('/projects'),
            providesTags: ['Project'],
        }),

        listBoards: build.query<TypeBoard[], void>({
            queryFn: () => readAll<TypeBoard>('/boards'),
            providesTags: ['Board'],
        }),

        listTags: build.query<TypeTag[], void>({
            queryFn: () => readAll<TypeTag>('/tags'),
            providesTags: ['Tag'],
        }),

        listTasks: build.query<TypeTask[], void>({
            queryFn: () => readAll<TypeTask>('/tasks', { archived: 'all' }),
            providesTags: ['Task'],
        }),

        createProject: build.mutation<TypeProject, { project: TypeProjectInput; column: string }>({
            query: ({ project, column }) => ({ url: '/projects', method: 'post', data: { project: projectBody(project), column } }),
            onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled;
                    writeProjects(dispatch, [data]);
                } catch {
                }
            },
        }),

        updateProject: build.mutation<TypeProject, { id: string; project: TypeProjectInput }>({
            query: ({ id, project }) => ({ url: `/projects/${id}`, method: 'patch', data: { project: projectBody(project) } }),
            onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled;
                    writeProjects(dispatch, [data]);
                } catch {
                }
            },
        }),

        removeProject: build.mutation<{ id: string }, string>({
            query: (id) => ({ url: `/projects/${id}`, method: 'delete' }),
            onQueryStarted: async (id, { dispatch, queryFulfilled }) => {
                try {
                    await queryFulfilled;
                    dispatch(workApi.util.updateQueryData('listProjects', undefined, (draft) => {
                        dropById(draft, id);
                    }));
                    dispatch(workApi.util.updateQueryData('listTags', undefined, (draft) => {
                        for (let index = draft.length - 1; index >= 0; index--) {
                            if (draft[index].projectId === id) draft.splice(index, 1);
                        }
                    }));
                } catch {
                }
            },
        }),

        createBoard: build.mutation<TypeBoard, TypeBoardInput>({
            query: (board) => ({ url: '/boards', method: 'post', data: { board: boardBody(board) } }),
            onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled;
                    writeBoards(dispatch, [data]);
                } catch {
                }
            },
        }),

        updateBoard: build.mutation<{ board: TypeBoard; projects: TypeProject[] }, { id: string; board: TypeBoardInput }>({
            query: ({ id, board }) => ({ url: `/boards/${id}`, method: 'patch', data: { board: boardBody(board) } }),
            onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled;
                    writeBoards(dispatch, [data.board]);
                    writeProjects(dispatch, data.projects);
                } catch {
                }
            },
        }),

        removeBoard: build.mutation<{ id: string; tags: number }, string>({
            query: (id) => ({ url: `/boards/${id}`, method: 'delete' }),
            onQueryStarted: async (id, { dispatch, queryFulfilled }) => {
                try {
                    await queryFulfilled;
                    dispatch(workApi.util.updateQueryData('listBoards', undefined, (draft) => {
                        dropById(draft, id);
                    }));
                } catch {
                }
            },
        }),

        placeProject: build.mutation<TypeProject[], { id: string; boardId: string; columnId: string; beforeId?: string | null }>({
            query: ({ id, boardId, columnId, beforeId }) => ({ url: `/projects/${id}/place`, method: 'patch', data: { boardId, columnId, beforeId: beforeId ?? '' } }),
            onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled;
                    writeProjects(dispatch, data);
                } catch {
                }
            },
        }),

        setProjectTags: build.mutation<TypeProject, { id: string; tags: string[] }>({
            query: ({ id, tags }) => ({ url: `/projects/${id}/tags`, method: 'patch', data: { tags } }),
            onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled;
                    writeProjects(dispatch, [data]);
                } catch {
                }
            },
        }),

        archiveProject: build.mutation<TypeProject, { id: string; archived: boolean }>({
            query: ({ id, archived }) => ({ url: `/projects/${id}/archive`, method: 'patch', data: { archived } }),
            onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled;
                    writeProjects(dispatch, [data]);
                } catch {
                }
            },
        }),

        createTag: build.mutation<TypeTag, { name: string; color?: TypeTagColor; projectId?: string | null }>({
            query: ({ name, color, projectId }) => ({ url: '/tags', method: 'post', data: { name, color: color ?? '', projectId: projectId ?? '' } }),
            onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled;
                    writeTags(dispatch, [data]);
                } catch {
                }
            },
        }),

        updateTag: build.mutation<{ tag: TypeTag; removedId: string | null; tasks: TypeTask[]; projects: TypeProject[] }, { id: string; name?: string; color?: TypeTagColor }>({
            query: ({ id, name, color }) => ({ url: `/tags/${id}`, method: 'patch', data: { name: name ?? '', color: color ?? '' } }),
            onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled;
                    writeTags(dispatch, [data.tag]);
                    if (data.removedId) dropTag(dispatch, data.removedId);
                    writeTasks(dispatch, data.tasks);
                    writeProjects(dispatch, data.projects);
                } catch {
                }
            },
        }),

        removeTag: build.mutation<{ id: string; tasks: TypeTask[]; projects: TypeProject[] }, string>({
            query: (id) => ({ url: `/tags/${id}`, method: 'delete' }),
            onQueryStarted: async (id, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled;
                    dropTag(dispatch, id);
                    writeTasks(dispatch, data.tasks);
                    writeProjects(dispatch, data.projects);
                } catch {
                }
            },
        }),

        createTask: build.mutation<TypeTask, TypeTaskInput>({
            query: (task) => ({
                url: '/tasks',
                method: 'post',
                data: {
                    task: {
                        projectId: task.projectId,
                        parentId: task.parentId ?? '',
                        title: task.title,
                        description: task.description ?? '',
                        notes: task.notes ?? '',
                        state: task.state,
                        priority: task.priority ?? 'normal',
                        assigneeId: task.assigneeId ?? '',
                        dueAt: task.dueAt ?? '',
                        estimateHours: task.estimateHours ?? null,
                        tags: task.tags ?? [],
                    },
                },
            }),
            onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled;
                    writeTasks(dispatch, [data]);
                } catch {
                }
            },
        }),

        updateTask: build.mutation<TypeTask, { id: string; task: TypeTaskPatch; seq?: number }>({
            query: ({ id, task }) => ({ url: `/tasks/${id}`, method: 'patch', data: { task: taskPatchBody(task) } }),
            onQueryStarted: async ({ id, task, seq }, { dispatch, queryFulfilled }) => {
                const patch = dispatch(workApi.util.updateQueryData('listTasks', undefined, (draft) => {
                    const entry = draft.find((item) => item.id === id);
                    if (entry) Object.assign(entry, task);
                }));

                try {
                    const { data } = await queryFulfilled;
                    writeTasks(dispatch, [data], seq);
                } catch {
                    patch.undo();
                }
            },
        }),

        editTask: build.mutation<{ tasks: TypeTask[]; dropped: number }, { id: string; task: TypeTaskEdit }>({
            query: ({ id, task }) => ({ url: `/tasks/${id}/edit`, method: 'patch', data: { task: taskEditBody(task) } }),
            onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled;
                    writeTasks(dispatch, data.tasks);
                } catch {
                }
            },
        }),

        removeTask: build.mutation<{ ids: string[]; tasks: TypeTask[] }, string>({
            query: (id) => ({ url: `/tasks/${id}`, method: 'delete' }),
            onQueryStarted: async (_id, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled;
                    dispatch(workApi.util.updateQueryData('listTasks', undefined, (draft) => {
                        const gone = new Set(data.ids);
                        for (let index = draft.length - 1; index >= 0; index--) {
                            if (gone.has(draft[index].id)) draft.splice(index, 1);
                        }
                    }));
                    writeTasks(dispatch, data.tasks);
                } catch {
                }
            },
        }),

        moveTask: build.mutation<TypeTask[], { id: string; state: TypeTaskState; beforeId?: string | null }>({
            query: ({ id, state, beforeId }) => ({ url: `/tasks/${id}/move`, method: 'patch', data: { state, beforeId: beforeId ?? '' } }),
            onQueryStarted: (arg, api) => settle(arg, api),
        }),

        placeTask: build.mutation<TypeTask[], { id: string; parentId: string | null; beforeId?: string | null }>({
            query: ({ id, parentId, beforeId }) => ({ url: `/tasks/${id}/place`, method: 'patch', data: { parentId: parentId ?? '', beforeId: beforeId ?? '' } }),
            onQueryStarted: (arg, api) => settle(arg, api),
        }),

        archiveTask: build.mutation<TypeTask[], { id: string; archived: boolean }>({
            query: ({ id, archived }) => ({ url: `/tasks/${id}/archive`, method: 'patch', data: { archived } }),
            onQueryStarted: (arg, api) => settle(arg, api),
        }),

    }),
});

const settle = async (_arg: unknown, { dispatch, queryFulfilled }: any) => {
    try {
        const { data } = await queryFulfilled;
        writeTasks(dispatch, data as TypeTask[]);
    } catch {
    }
};

export const {
    useCreateProjectMutation,
    useUpdateProjectMutation,
    useListProjectsQuery,
    useListBoardsQuery,
    useListTagsQuery,
    useListTasksQuery,
} = workApi;

export default workApi;
